import { FC } from 'react';
import type { FaixaDistribuicao } from '@/utils/equiplexSop';
import { fmtUn } from '@/utils/painelFormat';
import { visualFaixa, type ModoTema } from '../chartTheme';
import PainelCard from '../PainelCard';

interface Props {
  distribuicao: Record<FaixaDistribuicao, number>;
  limiares: { critico: number; alerta: number };
  modo: ModoTema;
  onFiltrar: (faixa: FaixaDistribuicao) => void;
}

/**
 * Quantos SKU×mês caem em cada faixa. Barras em HTML (quatro números não pedem
 * gráfico), cada faixa com ícone + rótulo + contagem; clicar filtra a tabela. Ocupa a largura
 * toda: as quatro faixas lado a lado na tela larga.
 */
const FaixasDesvioCard: FC<Props> = ({ distribuicao, limiares, modo, onFiltrar }) => {
  const nf = (v: number) => new Intl.NumberFormat('pt-BR').format(v);
  const faixas: { classe: FaixaDistribuicao; regra: string }[] = [
    { classe: 'critico', regra: `desvio acima de ${nf(limiares.critico)}%` },
    { classe: 'alerta', regra: `entre ${nf(limiares.alerta)}% e ${nf(limiares.critico)}%` },
    { classe: 'normal', regra: `até ${nf(limiares.alerta)}% para mais ou para menos` },
    { classe: 'sem_programa', regra: 'faturou, mas nenhuma OP começou no mês' },
  ];
  const comPrograma = distribuicao.critico + distribuicao.alerta + distribuicao.normal;
  const maior = Math.max(1, ...faixas.map((f) => distribuicao[f.classe]));

  return (
    <PainelCard
      testId="sop-faixas"
      titulo="Distribuição por faixa de desvio"
      subtitulo={`${fmtUn(comPrograma)} SKU×mês com programado no recorte. Clique numa faixa para filtrar a tabela.`}
    >
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {faixas.map(({ classe, regra }) => {
          const v = visualFaixa(classe, modo);
          const qtd = distribuicao[classe];
          return (
            <li key={classe}>
              <button
                type="button"
                onClick={() => onFiltrar(classe)}
                className="w-full rounded-lg px-2 py-1.5 text-left hover:bg-surface-hover"
                data-testid={`faixa-${classe}`}
              >
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex items-center gap-1.5 text-content-primary">
                    <span aria-hidden className="material-symbols-outlined text-[18px] leading-none" style={{ color: v.cor }}>
                      {v.icone}
                    </span>
                    <span className="font-medium">{v.rotulo}</span>
                  </span>
                  <span className="font-semibold tabular-nums text-content-primary">{fmtUn(qtd)}</span>
                </div>
                <div className="ml-6 text-xs text-content-muted">{regra}</div>
                <div className="mt-1.5 h-2 w-full rounded-sm bg-surface-hover" aria-hidden>
                  <div className="h-2 rounded-sm" style={{ width: `${(qtd / maior) * 100}%`, backgroundColor: v.cor }} />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </PainelCard>
  );
};

export default FaixasDesvioCard;
